'use client';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations as useTranslations$1, useLocale } from 'next-intl';
import { MessageCircle, X, Send } from '@/components/ui/icons';
import { DiscordIcon } from '@/components/icons';
import { TrackedDiscordLink } from '@/components/analytics/TrackedDiscordLink';
import { askDocsAssistant, DocsChatError, type DocsChatTurn } from '@/lib/api';
import { trackLearnAssistantAnswered, trackLearnAssistantClosed, trackLearnAssistantFailed, trackLearnAssistantOpened, trackLearnAssistantQuestionAsked } from '@/lib/analytics';
import { getLearnAssistantErrorKind, prepareLearnAssistantQuestion } from '@/lib/docs/assistantTelemetry';
import { useAuth } from '@/hooks/useAuth';
import type { AppLocale } from '@/i18n/routing';
// Brand name — verbatim in every locale.
const DISCORD_LABEL = 'Discord';
function currentPagePath(): string {
    return typeof window === 'undefined' ? '' : window.location.pathname;
}
/**
 * Learn/Docs "Ask AI" assistant panel. Slides in from the right and answers
 * Martini questions from the docs via the backend (POST /docs-chat/message).
 * Ported from martini-learn's DocsChat, wired to the Hono backend and localized.
 *
 * Rendered inside LearnHeader's right-hand action row: the Discord + Ask AI
 * pills sit in flow, the drawer stays fixed. Learn is public, so the Ask AI
 * pill only appears for real accounts — the backend requires one
 * (`requireRealAccount`, it spends on FAL), and offering signed-out readers a
 * button that can only fail is worse than not offering it. Discord is the
 * fallback everyone gets.
 */ export function DocsChat() {
    const t = useTranslations$1();
    const locale = useLocale() as AppLocale;
    const { auth, isAnonymous } = useAuth();
    const canAskAi = !!auth?.userId && !isAnonymous;
    const welcome = t("sPqpou");
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState<DocsChatTurn[]>([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    // After a failed turn, offer the Discord as the human fallback.
    const [errored, setErrored] = useState(false);
    const endRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const sendingRef = useRef(false);
    const sessionRef = useRef<{
        openedAt: number;
        turns: number;
    } | null>(null);
    useEffect(()=>{
        if (open) endRef.current?.scrollIntoView({
            behavior: 'smooth'
        });
    }, [
        messages,
        open,
        loading
    ]);
    useEffect(()=>{
        if (open) inputRef.current?.focus();
    }, [
        open
    ]);
    function errorMessage(err: unknown): string {
        const code = err instanceof DocsChatError ? err.code : 'UNKNOWN';
        if (code === 'RATE_LIMITED') {
            return t("JaWpt0");
        }
        if (code === 'TOO_LONG') {
            return t("ycitp8");
        }
        if (code === 'NETWORK') {
            return t("wQyJV1");
        }
        return t("YGOdq0");
    }
    function openAssistant() {
        sessionRef.current = {
            openedAt: Date.now(),
            turns: 0
        };
        trackLearnAssistantOpened({
            pagePath: currentPagePath(),
            locale,
            source: 'header'
        });
        setOpen(true);
    }
    function closeAssistant() {
        const session = sessionRef.current;
        if (session) {
            trackLearnAssistantClosed({
                pagePath: currentPagePath(),
                turns: session.turns,
                durationMs: Math.max(0, Date.now() - session.openedAt)
            });
        }
        sessionRef.current = null;
        setOpen(false);
    }
    function toggleAssistant() {
        if (open) closeAssistant();
        else openAssistant();
    }
    async function send() {
        const text = input.trim();
        // Synchronous ref guard: blocks a second submit that fires before React
        // re-renders with loading=true (which would post back-to-back user turns).
        if (!text || sendingRef.current) return;
        sendingRef.current = true;
        const pagePath = currentPagePath();
        const turnIndex = sessionRef.current?.turns ?? 0;
        if (sessionRef.current) sessionRef.current.turns += 1;
        const question = prepareLearnAssistantQuestion(text);
        trackLearnAssistantQuestionAsked({
            pagePath,
            locale,
            turnIndex,
            question: question.question,
            questionLength: question.questionLength
        });
        const requestStartedAt = Date.now();
        const next: DocsChatTurn[] = [
            ...messages,
            {
                role: 'user',
                content: text
            }
        ];
        setMessages(next);
        setInput('');
        setLoading(true);
        try {
            const reply = await askDocsAssistant(next, locale);
            setMessages((prev)=>[
                    ...prev,
                    {
                        role: 'assistant',
                        content: reply
                    }
                ]);
            setErrored(false);
            trackLearnAssistantAnswered({
                pagePath,
                turnIndex,
                latencyMs: Math.max(0, Date.now() - requestStartedAt),
                answerLength: reply.length
            });
        } catch (err) {
            setMessages((prev)=>[
                    ...prev,
                    {
                        role: 'assistant',
                        content: errorMessage(err)
                    }
                ]);
            setErrored(true);
            trackLearnAssistantFailed({
                pagePath,
                turnIndex,
                errorKind: getLearnAssistantErrorKind(err)
            });
        } finally{
            setLoading(false);
            sendingRef.current = false;
        }
    }
    // The canned welcome line is display-only; it's never sent to the backend.
    const thread = messages.length ? messages : [
        {
            role: 'assistant' as const,
            content: welcome
        }
    ];
    return (<>
      { /* Header actions — Discord + Ask AI side by side. */ }
      <div className="flex items-center gap-2">
        { /* Hidden below sm on purpose: the phone header keeps tabs, search,
            and the account CTA; Discord is reachable from the docs-chat error
            fallback and the marketing footer. */ }
        <TrackedDiscordLink source="learn_header" aria-label={DISCORD_LABEL} className="hidden h-9 shrink-0 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-[13px] font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground sm:inline-flex">
          <DiscordIcon className="h-4 w-4"/>
          <span className="hidden sm:inline">{DISCORD_LABEL}</span>
        </TrackedDiscordLink>
        {canAskAi && (<button onClick={toggleAssistant} aria-label={open ? t("99R8O_") : t("3odTbr")} className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-[13px] font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
            <MessageCircle className="h-4 w-4"/>
            <span className="hidden sm:inline">{t("E1J2uJ")}</span>
          </button>)}
      </div>

      {canAskAi && typeof document !== 'undefined' && createPortal(/* Portaled so the sticky header's backdrop filter cannot become the
             containing block for this viewport-fixed drawer. */ <div aria-hidden={!open} className={`fixed end-0 top-12 z-[55] flex h-[calc(100vh-48px)] w-full max-w-[380px] flex-col border-s border-border bg-background shadow-2xl transition-[transform,visibility] duration-300 ${open ? 'visible translate-x-0' : 'invisible pointer-events-none ltr:translate-x-full rtl:-translate-x-full'}`}>
            <div className="flex items-center justify-between border-b border-border/50 px-4 py-3">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-muted-foreground"/>
                <span className="text-[13px] font-semibold text-foreground">{t("e8-m20")}</span>
              </div>
              <button onClick={closeAssistant} aria-label={t("rbrahO")} className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground">
                <X className="h-4 w-4"/>
              </button>
            </div>

            <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
              {thread.map((m, i)=>(<div key={i} className={`max-w-[88%] whitespace-pre-line rounded-lg px-3.5 py-2.5 text-[13px] leading-relaxed ${m.role === 'assistant' ? 'self-start border border-border/40 bg-card/50 text-muted-foreground' : 'self-end bg-white/10 text-foreground'}`}>
                  {m.content}
                </div>))}
              {errored && !loading && (<TrackedDiscordLink source="docs_chat_fallback" className="inline-flex items-center gap-1.5 self-start rounded-lg border border-border/40 bg-card/50 px-3.5 py-2 text-[13px] text-muted-foreground transition-colors hover:bg-secondary/50 hover:text-foreground">
                  <DiscordIcon className="h-3.5 w-3.5"/>
                  {t("JQmbIq")}
                </TrackedDiscordLink>)}
              {loading && (<div className="self-start rounded-lg border border-border/40 bg-card/50 px-3.5 py-2.5 text-[13px] text-muted-foreground">
                  <span className="inline-flex gap-1">
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:-0.3s]"/>
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:-0.15s]"/>
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60"/>
                  </span>
                </div>)}
              <div ref={endRef}/>
            </div>

            <div className="flex gap-2 border-t border-border/50 p-3">
              <input ref={inputRef} value={input} onChange={(e)=>setInput(e.target.value)} onKeyDown={(e)=>{
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    }} placeholder={t("MAGaJs")} className="flex-1 rounded-lg border border-border/50 bg-card/30 px-3 py-2 text-[13px] text-foreground outline-none transition-colors placeholder:text-muted-foreground/40 focus:border-border"/>
              <button onClick={send} disabled={loading || !input.trim()} aria-label={t("9WRlF4")} className="flex items-center justify-center rounded-lg bg-white/10 px-3 py-2 text-foreground transition-colors hover:bg-white/15 disabled:opacity-40">
                <Send className="h-3.5 w-3.5"/>
              </button>
            </div>
          </div>, document.body)}
    </>);
}
